import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Fuel } from "lucide-react";

export const STATUS_LABELS = {
  pendiente: "Pendiente",
  recogido: "Recogido",
  en_proceso: "En proceso",
  listo: "Listo",
  entregado: "Entregado",
};

const STATUS_COLORS = {
  pendiente: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
  recogido: "bg-blue-100 text-blue-800 hover:bg-blue-100",
  en_proceso: "bg-purple-100 text-purple-800 hover:bg-purple-100",
  listo: "bg-orange-100 text-orange-800 hover:bg-orange-100",
  entregado: "bg-green-100 text-green-800 hover:bg-green-100",
};

const PAID_BADGE = "bg-green-100 text-green-800 hover:bg-green-100";
const UNPAID_BADGE = "bg-slate-100 text-slate-600 hover:bg-slate-100";

const DeliveryBadge = ({ order }) => (
  <p className="text-xs mt-1 inline-flex items-center gap-1 rounded-full bg-cyan-50 text-cyan-700 px-2 py-0.5 font-medium"
    data-testid={`order-delivery-${order.order_number}`}>
    <Fuel className="h-3 w-3" />
    {order.delivery.distance_km.toLocaleString("es-ES", { maximumFractionDigits: 1 })} km ·{" "}
    {order.delivery.fee === 0 ? "gasolina gratis" : `gasolina ${order.delivery.fee.toFixed(2)} €`}
  </p>
);

const OrderRow = ({ order: o, onStatusChange }) => (
  <TableRow data-testid={`order-row-${o.order_number}`}>
    <TableCell className="font-medium">{o.order_number}</TableCell>
    <TableCell>
      <p className="font-medium">{o.customer.name}</p>
      <p className="text-xs text-slate-500">{o.customer.phone}</p>
      <p className="text-xs text-slate-500">{o.customer.address}</p>
      {o.delivery && <DeliveryBadge order={o} />}
    </TableCell>
    <TableCell className="text-sm">
      {o.items.map((i) => <p key={i.service_id}>{i.name} x{i.quantity}</p>)}
    </TableCell>
    <TableCell className="text-sm">{o.pickup_date}</TableCell>
    <TableCell className="font-semibold">{o.total.toFixed(2)} €</TableCell>
    <TableCell>
      <Badge className={o.payment_status === "pagado" ? PAID_BADGE : UNPAID_BADGE}>
        {o.payment_status === "pagado" ? "Pagado" : "Pendiente"}
      </Badge>
    </TableCell>
    <TableCell>
      <Select value={o.status} onValueChange={(v) => onStatusChange(o.id, v)}>
        <SelectTrigger className="w-36 h-9" data-testid={`status-select-${o.order_number}`}>
          <SelectValue>
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value} data-testid={`status-option-${value}`}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TableCell>
  </TableRow>
);

export const OrdersTable = ({ orders, onStatusChange }) => (
  <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="orders-table">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Pedido</TableHead>
          <TableHead>Cliente</TableHead>
          <TableHead>Servicios</TableHead>
          <TableHead>Recogida</TableHead>
          <TableHead>Total</TableHead>
          <TableHead>Pago</TableHead>
          <TableHead>Estado</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.length === 0 && (
          <TableRow>
            <TableCell colSpan={7} className="text-center text-slate-500 py-10">Todavía no hay pedidos.</TableCell>
          </TableRow>
        )}
        {orders.map((o) => <OrderRow key={o.id} order={o} onStatusChange={onStatusChange} />)}
      </TableBody>
    </Table>
  </div>
);
